import { axiosDelete, axiosGet, axiosPatch, axiosPost } from '@/api/handlers';
import { useMutation, useQuery } from '@/api/hooks';
import {
  IndirectCostsMutationSchema,
  PaymentMutationParamsSchema,
  PaymentsModel,
} from '@/features/project/projects/_models/payment';
import { useInvalidateQuery } from '@/hooks';
import { z } from 'zod';
import { queryKeyMap } from '../../../../_utils/query-keys';

export const useContractorPayments = <T = z.infer<typeof PaymentsModel>>({
  projectId,
  select,
  enabled = true,
}: {
  projectId: string;
  select?: (data: z.infer<typeof PaymentsModel>) => T;
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: queryKeyMap.contractorPayments(projectId),
    queryFn: async () => {
      const payments = await axiosGet(
        `/api/projects/${projectId}/contractor-payments/`,
      );
      return PaymentsModel.parse(payments);
    },
    select,
    enabled: enabled && !!projectId,
  });
};

export const useCreatePayment = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (values: z.infer<typeof PaymentMutationParamsSchema>) => {
      const { projectId, paymentGroupId, ...payload } =
        PaymentMutationParamsSchema.parse({
          ...values,
          isOthers: !!values.isOthers,
          corporateNumber: values.isOthers ? null : values.corporateNumber,
          corporateAddress: values.isOthers ? null : values.corporateAddress,
          corporateKind: values.isOthers ? null : values.corporateKind,
        });
      const res = await axiosPost(
        `/api/projects/${projectId}/payment-groups/${paymentGroupId}/payments/`,
        payload,
      );
      await Promise.all([
        invalidate(queryKeyMap.paymentGroups(projectId)),
        invalidate(queryKeyMap.payments()),
        invalidate(queryKeyMap.contractorPayments(projectId)),
        invalidate(queryKeyMap.paymentGraphData(projectId)),
      ]);
      return res;
    },
  });
};

export const useUpdatePayment = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (
      values: z.infer<typeof PaymentMutationParamsSchema> & {
        id: string;
      },
    ) => {
      const { id, projectId, paymentGroupId, ...payload } =
        PaymentMutationParamsSchema.extend({
          id: z.string().uuid(),
        }).parse({
          ...values,
          isOthers: !!values.isOthers,
          corporateNumber: values.isOthers ? null : values.corporateNumber,
          corporateAddress: values.isOthers ? null : values.corporateAddress,
          corporateKind: values.isOthers ? null : values.corporateKind,
        });
      const response = await axiosPatch(
        `/api/projects/${projectId}/payment-groups/${paymentGroupId}/payments/${id}/`,
        payload,
      );
      await Promise.all([
        invalidate(queryKeyMap.paymentGroups(projectId)),
        invalidate(queryKeyMap.payments()),
        invalidate(queryKeyMap.contractorPayments(projectId)),
        invalidate(queryKeyMap.paymentGraphData(projectId)),
      ]);
      return response;
    },
  });
};

export const useDeletePayment = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async ({
      id,
      projectId,
      paymentGroupId,
    }: {
      id: string;
      projectId: string;
      paymentGroupId: string;
    }) => {
      const response = await axiosDelete(
        `/api/projects/${projectId}/payment-groups/${paymentGroupId}/payments/${id}/`,
      );
      await Promise.all([
        invalidate(queryKeyMap.paymentGroups(projectId)),
        invalidate(queryKeyMap.payments()),
        invalidate(queryKeyMap.contractorPayments(projectId)),
        invalidate(queryKeyMap.paymentGraphData(projectId)),
      ]);
      return response;
    },
  });
};

export const useUpdateIndirectCosts = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (values: z.infer<typeof IndirectCostsMutationSchema>) => {
      const { projectId, ...payload } =
        IndirectCostsMutationSchema.parse(values);

      const response = await axiosPatch(`/api/projects/${projectId}/`, payload);
      await Promise.all([
        invalidate(queryKeyMap.project(projectId)),
        invalidate(queryKeyMap.paymentGroups(projectId)),
        invalidate(queryKeyMap.paymentGraphData(projectId)),
      ]);
      return response;
    },
  });
};
